import type { ResolvedPolicy } from "./policy"
import type { Baustein } from "./types"

/**
 * What a Baustein costs, in dollars, per participant.
 *
 * Phillips' ROI methodology is a conversation about money, so the composer needs
 * a price before it can rank by value per dollar rather than value per hour. The
 * catalog does not carry one — and deliberately still does not.
 *
 * A rate card beats 51 invented prices for four reasons:
 *
 *  1. Nobody has to defend a number. "Why is Verhandlungsführung exactly
 *     USD 1,340?" has no good answer in a demo. "We price Präsenz at USD 150 an
 *     hour plus USD 100 fixed" has an excellent one, and it is how L&D actually
 *     budgets.
 *  2. The rate card is itself the HR lever, so it belongs on the settings page
 *     next to the ranking mode rather than buried in a data file.
 *  3. It travels in the policy, so "which prices produced this ranking" is
 *     answered by the same object that answers everything else.
 *  4. The catalog is 21 Präsenz / 18 Webinar / 12 E-Learning, so at 150/60/25
 *     flipping the denominator reorders the plan dramatically and in a direction
 *     anyone in the room can predict — which is what makes a live knob
 *     persuasive rather than magic.
 *
 * `Baustein.costUsd` overrides the computed figure when a real price is known,
 * which is also how a later LMS integration supplies real per-course prices
 * without an engine change.
 */
export function bausteinCostUsd(baustein: Baustein, policy: ResolvedPolicy): number {
    const { cost } = policy
    const hours = baustein.durationHours

    const direct =
        cost.honourCatalogOverride && typeof baustein.costUsd === "number"
            ? baustein.costUsd
            : (cost.perHourUsdByFormat[baustein.format] ?? 0) * hours +
              (cost.fixedUsdByFormat[baustein.format] ?? 0)

    // The learner's time is a real cost even when the course is free — that is
    // the whole point of counting Selbststudium and On-the-job honestly. Off by
    // default because charging for it is a decision, not a fact.
    return direct + cost.opportunityUsdPerHour * hours
}
